import React, { useState, useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { fetchDashboardData } from '../services/dashboardService';
import '../styles/DashboardOverview.css';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const DashboardOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const chartInstanceRef = useRef(null);

  // Cleanup Chart.js instance on unmount
  useEffect(() => {
    return () => {
      console.log('[DashboardOverview] Unmounting - destroying chart instance');
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
      setData(null);
      setSelectedService(null);
    };
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const dashboardData = await fetchDashboardData();
        if (dashboardData.success) {
          setData(dashboardData.data);
        } else {
          setError('Failed to load dashboard data');
        }
      } catch (err) {
        setError('Error loading dashboard data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const getServiceTypeDistribution = () => {
    if (!data?.activeServices) return null;
    
    const distribution = data.activeServices.reduce((acc, service) => {
      acc[service.type] = (acc[service.type] || 0) + 1;
      return acc;
    }, {});

    return {
      labels: Object.keys(distribution).map(key => key.charAt(0).toUpperCase() + key.slice(1)),
      datasets: [
        {
          data: Object.values(distribution),
          backgroundColor: [
            'rgba(59, 130, 246, 0.6)', // Blue
            'rgba(16, 185, 129, 0.6)', // Green
            'rgba(245, 158, 11, 0.6)', // Amber
            'rgba(239, 68, 68, 0.6)', // Red
            'rgba(139, 92, 246, 0.6)', // Purple
          ],
          borderColor: [
            'rgba(59, 130, 246, 1)',
            'rgba(16, 185, 129, 1)',
            'rgba(245, 158, 11, 1)',
            'rgba(239, 68, 68, 1)',
            'rgba(139, 92, 246, 1)',
          ],
          borderWidth: 1,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: document.documentElement.getAttribute('data-theme') === 'dark' ? '#f4f4f5' : '#18181b',
        }
      },
    },
  };

  if (loading) {
    return (
      <div className="loading-spinner-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        {error}
        <button onClick={() => window.location.reload()} className="btn btn-secondary mt-2">
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="dashboard-overview">
        {/* Metrics Grid */}
        <div className="metrics-grid">
          <div className="metric-card">
            <div className="metric-content">
              <span className="metric-label">Total Requests</span>
              <span className="metric-value">{data?.totalRequests || 0}</span>
              <div className="metric-trend trend-up">
                <i className="fas fa-arrow-up"></i>
                <span>All time</span>
              </div>
            </div>
            <div className="metric-icon">
              <i className="fas fa-clipboard-list"></i>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-content">
              <span className="metric-label">Active Bots</span>
              <span className="metric-value">{data?.totalBots || 0}</span>
              <div className="metric-trend trend-up">
                <i className="fas fa-robot"></i>
                <span>Running now</span>
              </div>
            </div>
            <div className="metric-icon">
              <i className="fas fa-project-diagram"></i>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-content">
              <span className="metric-label">Average SPI</span>
              <span className="metric-value">
                {data?.averageSPI ? data.averageSPI.toFixed(2) : '0.00'}
              </span>
              <div className="metric-trend">
                <span className="text-muted">Market Index</span>
              </div>
            </div>
            <div className="metric-icon">
              <i className="fas fa-chart-line"></i>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="charts-section">
          <div className="chart-card">
            <div className="chart-header">
              <h3 className="chart-title">Services Distribution</h3>
            </div>
            <div style={{ height: '300px', display: 'flex', justifyContent: 'center' }}>
              {data?.activeServices?.length > 0 ? (
                <Doughnut 
                  data={getServiceTypeDistribution()} 
                  options={chartOptions}
                  ref={(ref) => {
                    if (ref) {
                      chartInstanceRef.current = ref;
                    }
                  }}
                />
              ) : (
                <div className="no-data-message">No active services data available</div>
              )}
            </div>
          </div>
        </div>

        {/* Active Services Table */}
        <div className="services-section">
          <div className="section-header">
            <h3 className="section-title">Active Services</h3>
          </div>
          
          <div className="services-table-container">
            <table className="services-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Search Term</th>
                  <th>Providers</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data?.activeServices?.length > 0 ? (
                  data.activeServices.map((service, index) => (
                    <tr key={index} onClick={() => setSelectedService(service)}>
                      <td>
                        <span className="type-badge">
                          {service.type}
                        </span>
                      </td>
                      <td>{service.search_term}</td>
                      <td>
                        <div className="provider-list">
                          {service.providers && service.providers.slice(0, 3).map((provider, i) => (
                            <span key={i} className="provider-pill">{provider}</span>
                          ))}
                          {service.providers && service.providers.length > 3 && (
                            <span className="provider-pill">+{service.providers.length - 3}</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge ${service.active ? 'status-active' : 'status-inactive'}`}>
                          {service.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center">No active services found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Service Details Modal */}
      {selectedService && (
        <div className="modal-overlay" onClick={() => setSelectedService(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Service Details</h3>
              <button className="close-btn" onClick={() => setSelectedService(null)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="service-details-modal">
              <div className="service-detail-row">
                <span className="detail-label">Type</span>
                <span className="detail-value">{selectedService.type}</span>
              </div>
              <div className="service-detail-row">
                <span className="detail-label">Search Term</span>
                <span className="detail-value">{selectedService.search_term}</span>
              </div>
              <div className="service-detail-row">
                <span className="detail-label">Status</span>
                <span className={`status-badge ${selectedService.active ? 'status-active' : 'status-inactive'}`}>
                  {selectedService.active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="mt-4">
                <span className="detail-label block mb-2">Providers</span>
                <div className="provider-list">
                  {selectedService.providers && selectedService.providers.map((provider, i) => (
                    <span key={i} className="provider-tag">{provider}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={() => setSelectedService(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardOverview;
